import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpParams } from '@angular/common/http';

// PrimeNG Modules
import { ButtonModule, ButtonSeverity } from 'primeng/button';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SplitButtonModule } from 'primeng/splitbutton';

// Models and Services
import { PendingApprovalSummary, WorkflowApproval } from '../../chat-widgets/approval-card/workflow-apporval.model';
import { WorkflowApprovalService } from '../../chat-widgets/approval-card/workflow-approval.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AppUser } from '../../users/user.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { InitiateApprovalRequest } from '../../chat-widgets/approval-card/initiate-approval.model';
import { SelectButtonModule } from "primeng/selectbutton";
import { Resource } from '../../master-data/resource/resource.model';
import { ResourceService } from '../../master-data/resource/resource.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-approval-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
    DialogModule,
    BadgeModule,
    ToastModule,
    SplitButtonModule,
    SelectButtonModule,
    AppDatePipe
  ],
  providers: [MessageService],
  templateUrl: './approval-list.component.html',
  styleUrls: ['./approval-list.component.css']
})
export class ApprovalListComponent implements OnInit {

  @Input() display: 'inline' | 'dialog' = 'dialog';
  pendingApprovals: PendingApprovalSummary[] = [];
  loading = false;
  totalRecords = 0;
  readonly pageSize = 50;
  currentFirst = 0;
  currentUser: AppUser | null = null;
  listDialogVisible = false;

  filter: string = '';
  filterOptions: any[] = [];
  resourceList: Resource[] = [];


  constructor(
    private workflowApprovalService: WorkflowApprovalService,
    private authService: AuthenticationService,
    private router: Router,
    private resourceService: ResourceService,
    private messageService: MessageService,
    private chatSessionService: ChatSessionService
  ) {
    this.filterOptions = [
      { label: 'Settlement', value: 'Settlement' },
      { label: 'Promo Plan', value: 'Promo Plan' },
      { label: 'Fixed Rebate', value: 'Fixed Rebate' },
      { label: 'Progressive Rebate', value: 'Progressive Rebate' }
    ];
  }

  ngOnInit(): void {
    this.filter = this.filterOptions[0].value;
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user && user.id) {
        let resourceParam = new HttpParams()
          .set('page', '0')
          .set('size', '1000');

        this.resourceService.getAllResources(resourceParam).pipe(
          switchMap(resources => {
            this.resourceList = resources.content;
            return this.loadPendingApprovals();
          })
        ).subscribe(approvals => {
          this.pendingApprovals = approvals;
        });
      }
    });
  }

  loadPendingApprovals(page = 0): Observable<PendingApprovalSummary[]> {

    this.loading = true;
    const requiredRoleId = this.currentUser?.roles.at(0)?.id;
    const resourceTypeId = this.resourceList.find(r => r.resourceName === this.filter)?.id;

    if (!requiredRoleId || !resourceTypeId) {
      this.loading = false;
      this.totalRecords = 0;
      return of([]);
    }

    return this.workflowApprovalService.getPendingApprovalSummaries(
      requiredRoleId,
      resourceTypeId,
      page,
      this.pageSize
    ).pipe(
      map(page => {
        this.totalRecords = page.totalElements;
        return page.content;
      }),
      catchError((err) => {
        console.error('Error loading pending approvals', err);
        return of([]);
      }),
      finalize(() => this.loading = false)
    );
  }

  reload(): void {
    this.currentFirst = 0;
    this.loadPendingApprovals().subscribe(approvals => {
      this.pendingApprovals = approvals;
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (!this.currentUser || this.resourceList.length === 0) {
      return;
    }

    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.currentFirst = first;
    this.loadPendingApprovals(Math.floor(first / rows)).subscribe(approvals => {
      this.pendingApprovals = approvals;
    });
  }

  showListDialog() {
    this.listDialogVisible = true;
  }

  getPendingApprovalCount(): number {
    return this.totalRecords;
  }

  takeAction(approval: WorkflowApproval, action: 'APPROVED' | 'REJECTED'): void {
    if (!approval.id) return;

    this.workflowApprovalService.recordAction(approval.id, { actionTaken: action, comments: 'Action taken from approvals list' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Action ${action.toLowerCase()} recorded.` });
        if (this.currentUser && this.currentUser.id) {
          this.loadPendingApprovals().subscribe(approvals => this.pendingApprovals = approvals);
        };
        const initiateApprovalRequest: InitiateApprovalRequest = {
          resourceTypeId: approval.resourceType?.id ?? 0,
          businessObjectId: approval.businessObjectId ?? 0,
          currentWorkflowStatusId: approval.workflowStatus?.id ?? 0
        }
        this.workflowApprovalService.initiateApprovals(initiateApprovalRequest).subscribe(initiate => {
          console.log('initiate approvals done');
        });
      },
      error: (err) => {
        console.error('Error taking action', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to record action.' });
      }
    });
  }

  navigateToItem(approval: PendingApprovalSummary): void {
    if (approval.resourceType && approval.businessObjectId) {
      let path: (string | number)[] = [];
      let resourceId = -1;
      switch (approval.resourceType.resourceName) {
        case 'Promo Plan':
          path = ['promo-plan', 'view', approval.businessObjectId];
          resourceId = approval.resourceType.id ?? -1;
          break;
        case 'Sellout':
          path = ['sellout', 'view', approval.businessObjectId];
          resourceId = approval.resourceType.id ?? -1;
          break;
        case 'Settlement':
          path = ['settlement', 'view', approval.businessObjectId];
          resourceId = approval.resourceType.id ?? -1;
          break;
        case 'Fixed Rebate':
          path = ['rebate', 'view', approval.businessObjectId];
          resourceId = approval.resourceType.id ?? -1;
          break;
        // Add other cases as needed
      }

      if (path.length > 0 && resourceId !== -1) {
        this.loading = true;
        this.chatSessionService.searchSession(resourceId, approval.businessObjectId).subscribe(session => {
          this.loading = false;
          if (session && session.sessionId) {
            this.router.navigate(['/ai-studio', 'session', session.sessionId, { outlets: { canvasOutlet: path } }]);
            this.listDialogVisible = false; // Close dialog on navigation
          } else {
            // Handle case where session is not found
            this.chatSessionService.sessions$.pipe(take(1)).subscribe(sessions => {
              if (sessions.length > 0) {
                const firstSessionId = sessions[0].sessionId;
                this.router.navigate(['/ai-studio', 'session', firstSessionId, { outlets: { canvasOutlet: path } }]);
                this.listDialogVisible = false; // Close dialog on navigation
              } else {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No active chat session found.' });
              }
            });
          }
        });
      }
    }
  }

  getSeverityForStatus(approval: WorkflowApproval | undefined): ButtonSeverity {
    return approval?.isSuccessAction ? 'success' : 'danger';
  }

}
